<script lang="ts">
  import { useModalAnimation } from '../lib/useModalAnimation.svelte';

  interface Props {
    open: boolean;
    action: 'complete' | 'remove';
    taskTitle: string;
    refillSameDay: boolean;
    onCancel: () => void;
    onConfirm: () => void;
  }

  let { open, action, taskTitle, refillSameDay, onCancel, onConfirm }: Props = $props();

  const modal = useModalAnimation(
    () => open,
    () => onCancel(),
  );

  let title = $derived(action === 'complete' ? 'Mark this task done?' : 'Remove from Now?');
  let confirmLabel = $derived(action === 'complete' ? 'Mark done' : 'Remove');
  let explanation = $derived.by(() => {
    if (action === 'complete') {
      return refillSameDay
        ? 'It will leave Now and free the slot immediately, so you can add another task today.'
        : 'It will leave Now but keep occupying a slot until tomorrow.';
    }
    return 'The task stays on its project. The Now slot frees immediately so you can add something else.';
  });

  function handleKeydown(e: KeyboardEvent) {
    if (!open) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      modal.animateClose();
    }
  }
</script>

<svelte:window onkeydown={open ? handleKeydown : undefined} />

{#if open}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="modal-overlay"
    class:closing={modal.closing}
    bind:this={modal.overlayEl}
    onclick={modal.animateClose}
    role="presentation"
  >
    <div class="modal-card" onclick={(e) => e.stopPropagation()} role="dialog" tabindex="-1">
      <div class="modal-header">
        <h2 class="modal-title">{title}</h2>
      </div>
      <div class="modal-body">
        <p class="task-name">{taskTitle}</p>
        <p class="modal-subtext">{explanation}</p>
      </div>
      <div class="modal-actions">
        <button type="button" class="btn btn-cancel" onclick={modal.animateClose}>Cancel</button>
        <button
          type="button"
          class="btn"
          class:btn-confirm={action === 'complete'}
          class:btn-remove={action === 'remove'}
          onclick={onConfirm}>{confirmLabel}</button
        >
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 320;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.45);
    animation: overlay-in 150ms ease-out both;
  }

  .modal-overlay.closing {
    animation: overlay-out 100ms ease-in both;
  }

  .modal-overlay.closing .modal-card {
    animation: card-out 100ms ease-in both;
  }

  .modal-card {
    width: min(24rem, calc(100vw - 2rem));
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.2);
    animation: card-in 150ms ease-out both;
  }

  .modal-header {
    padding: 1rem 1.1rem 0.25rem;
  }

  .modal-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #111827;
  }

  .modal-body {
    padding: 0.5rem 1.1rem 0.25rem;
  }

  .task-name {
    margin: 0 0 0.4rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
  }

  .modal-subtext {
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.45;
    color: #6b7280;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 0.9rem 1.1rem 1rem;
  }

  .btn {
    border-radius: 0.375rem;
    padding: 0.4rem 0.75rem;
    font: inherit;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
  }

  .btn-cancel {
    border: 1px solid #d1d5db;
    background: white;
    color: #374151;
  }

  .btn-confirm {
    border: 1px solid #86efac;
    background: #dcfce7;
    color: #166534;
  }

  .btn-remove {
    border: 1px solid #fde68a;
    background: #fffbeb;
    color: #92400e;
  }

  @keyframes overlay-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes overlay-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @keyframes card-in {
    from {
      opacity: 0;
      transform: translateY(6px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes card-out {
    from {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    to {
      opacity: 0;
      transform: translateY(6px) scale(0.98);
    }
  }
</style>
